import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { sprintService, projectService } from "../services/edosService";

export default function SprintCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const defaultProjectId = searchParams.get("projectId") || "";

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    projectId: defaultProjectId,
    sprintName: "",
    startDate: "",
    endDate: "",
    sprintGoal: "",
    plannedStoryPoints: "",
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch {
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async () => {
    if (
      !form.projectId ||
      !form.sprintName ||
      !form.startDate ||
      !form.endDate
    ) {
      toast({
        title: "Project, sprint name, start and end dates are required",
        status: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.plannedStoryPoints) {
        payload.plannedStoryPoints = Number(payload.plannedStoryPoints);
      } else {
        delete payload.plannedStoryPoints;
      }

      const res = await sprintService.create(payload);
      const created = res?.data?.data || res?.data?.sprint || res?.data;
      toast({ title: "Sprint created", status: "success" });

      if (created?._id) {
        navigate(`/edos/sprints/${created._id}`);
      } else {
        navigate(`/edos/sprints?projectId=${form.projectId}`);
      }
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to create sprint",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={6}>
      <HStack mb={5} spacing={3}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={() =>
            navigate(
              `/edos/sprints${form.projectId ? `?projectId=${form.projectId}` : ""}`,
            )
          }
        >
          Back
        </Button>
        <Text fontSize="2xl" fontWeight="bold">
          Create Sprint
        </Text>
      </HStack>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        maxW="760px"
      >
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel>Project</FormLabel>
            <Select
              value={form.projectId}
              onChange={(e) =>
                setForm((f) => ({ ...f, projectId: e.target.value }))
              }
            >
              <option value="">Select project...</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectName}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Sprint Name</FormLabel>
            <Input
              value={form.sprintName}
              onChange={(e) =>
                setForm((f) => ({ ...f, sprintName: e.target.value }))
              }
              placeholder="e.g. Sprint 1"
            />
          </FormControl>

          <HStack align="start" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Start Date</FormLabel>
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, startDate: e.target.value }))
                }
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>End Date</FormLabel>
              <Input
                type="date"
                value={form.endDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, endDate: e.target.value }))
                }
              />
            </FormControl>
          </HStack>

          <FormControl>
            <FormLabel>Sprint Goal</FormLabel>
            <Input
              value={form.sprintGoal}
              onChange={(e) =>
                setForm((f) => ({ ...f, sprintGoal: e.target.value }))
              }
              placeholder="What does this sprint aim to achieve?"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Planned Story Points</FormLabel>
            <Input
              type="number"
              value={form.plannedStoryPoints}
              onChange={(e) =>
                setForm((f) => ({ ...f, plannedStoryPoints: e.target.value }))
              }
              placeholder="e.g. 40"
            />
          </FormControl>

          <HStack justify="flex-end" pt={2}>
            <Button
              variant="ghost"
              onClick={() =>
                navigate(
                  `/edos/sprints${form.projectId ? `?projectId=${form.projectId}` : ""}`,
                )
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              isLoading={submitting}
              onClick={handleCreate}
            >
              Create Sprint
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}
